import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getCalibrationCertificates,
  getCalibrationCertificateSummary,
  getCalibrationOrganisations,
  getCalibrationCertificate,
  getCalibrationCertificateDetail,
  lockCalibrationCertificate,
  unlockCalibrationCertificate,
} from '../api';
import type { CalibrationCertificateFilters } from '../types';

function describeLockError(err: unknown): string {
  if (err && typeof err === 'object' && 'status' in err) {
    const e = err as { status?: number; data?: unknown; message?: string };
    const bodyText =
      typeof e.data === 'string'
        ? e.data
        : e.data && typeof e.data === 'object'
          ? JSON.stringify(e.data)
          : '';
    if (e.status === 401) {
      return bodyText
        ? `401 Unauthorized — ${bodyText}`
        : 'Session not recognised by /v3 (legacy session blob missing).';
    }
    if (e.status === 403) {
      return bodyText
        ? `403 Forbidden — ${bodyText}`
        : 'Requires the calibrationrecords.modify permission.';
    }
    if (e.status) return `${e.status} — ${e.message || bodyText || 'Error'}`;
  }
  return err instanceof Error ? err.message : String(err);
}

// Query key factory for consistent cache management
export const calibrationCertificateKeys = {
  all: ['calibration-certificates'] as const,
  list: (filters?: CalibrationCertificateFilters) =>
    [...calibrationCertificateKeys.all, 'list', filters] as const,
  summary: (organisationId?: string, accountId?: string) =>
    [...calibrationCertificateKeys.all, 'summary', { organisationId, accountId }] as const,
  organisations: () => [...calibrationCertificateKeys.all, 'organisations'] as const,
  detail: (id: string) => [...calibrationCertificateKeys.all, 'detail', id] as const,
  fullDetail: (mongoId: string) => [...calibrationCertificateKeys.all, 'fullDetail', mongoId] as const,
};

/**
 * Hook to fetch calibration certificates with filtering and pagination
 */
export function useCalibrationCertificates(filters: CalibrationCertificateFilters = {}) {
  return useQuery({
    queryKey: calibrationCertificateKeys.list(filters),
    queryFn: () => getCalibrationCertificates(filters),
  });
}

/**
 * Hook to fetch calibration certificates summary statistics
 */
export function useCalibrationSummary(options?: { organisationId?: string; accountId?: string; showAll?: boolean }) {
  return useQuery({
    queryKey: calibrationCertificateKeys.summary(options?.organisationId, options?.accountId),
    queryFn: () => getCalibrationCertificateSummary(options),
  });
}

/**
 * Hook to fetch organisations with calibration certificates
 */
export function useCalibrationOrganisations(options?: {
  showAll?: boolean;
  accountId?: string;
  dueInDays?: number;
  dueDateFrom?: string;
  dueDateTo?: string;
  includeOverdue?: boolean;
  status?: string | string[];
}) {
  return useQuery({
    queryKey: [...calibrationCertificateKeys.organisations(), options || {}],
    queryFn: () => getCalibrationOrganisations(options),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single calibration certificate
 */
export function useCalibrationCertificate(id: string) {
  return useQuery({
    queryKey: calibrationCertificateKeys.detail(id),
    queryFn: () => getCalibrationCertificate(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch full certificate details from MongoDB for printable view
 * @param mongoId - The MongoDB _id of the calibration record
 */
export function useCalibrationCertificateDetail(mongoId: string | null) {
  return useQuery({
    queryKey: calibrationCertificateKeys.fullDetail(mongoId || ''),
    queryFn: () => getCalibrationCertificateDetail(mongoId!),
    enabled: !!mongoId,
  });
}

/**
 * Lock/unlock calibration certificates. Accepts a single mongoId or an array
 * for bulk operations. Bulk runs sequentially so partial failures (e.g. 403 on
 * one cert) don't abort the whole batch.
 */
export function useToggleCalibrationLock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      mongoIds,
      lock,
    }: {
      mongoIds: string[];
      lock: boolean;
    }) => {
      const op = lock ? lockCalibrationCertificate : unlockCalibrationCertificate;
      const results = await Promise.allSettled(mongoIds.map((id) => op(id)));
      const failures = results
        .map((r, i) => ({ r, id: mongoIds[i] }))
        .filter(({ r }) => r.status === 'rejected');
      return {
        succeeded: mongoIds.length - failures.length,
        failed: failures.length,
        firstError:
          failures.length > 0
            ? describeLockError((failures[0].r as PromiseRejectedResult).reason)
            : null,
      };
    },
    // Optimistic update: PG mirror via CDC has lag, so we flip `locked` in the
    // cache immediately. onError restores the snapshot if the write failed.
    onMutate: async ({ mongoIds, lock }) => {
      await queryClient.cancelQueries({ queryKey: calibrationCertificateKeys.all });
      const idSet = new Set(mongoIds);
      const snapshots = queryClient.getQueriesData({ queryKey: calibrationCertificateKeys.all });

      queryClient.setQueriesData<{ items?: Array<{ mongoCalibrationId?: string; locked?: boolean; locked_date?: string | null }> }>(
        { queryKey: calibrationCertificateKeys.all },
        (old) => {
          if (!old || !Array.isArray(old.items)) return old;
          return {
            ...old,
            items: old.items.map((c) =>
              c.mongoCalibrationId && idSet.has(c.mongoCalibrationId)
                ? { ...c, locked: lock, locked_date: lock ? new Date().toISOString() : null }
                : c,
            ),
          };
        },
      );

      return { snapshots };
    },
    onSuccess: ({ succeeded, failed, firstError }, vars) => {
      // Defer invalidate so CDC has a moment to mirror Mongo → PG before the
      // refetch undoes the optimistic flip.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: calibrationCertificateKeys.all });
      }, 2000);
      const verb = vars.lock ? 'locked' : 'unlocked';
      if (failed === 0) {
        toast.success(
          succeeded === 1
            ? `Certificate ${verb}`
            : `${succeeded} certificates ${verb}`,
        );
      } else if (succeeded === 0) {
        toast.error(`Failed to ${vars.lock ? 'lock' : 'unlock'}`, {
          description: firstError || undefined,
        });
      } else {
        toast.warning(`${succeeded} ${verb}, ${failed} failed`, {
          description: firstError || undefined,
        });
      }
    },
    onError: (err, vars, context) => {
      // Roll back optimistic update.
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      toast.error(`Failed to ${vars.lock ? 'lock' : 'unlock'}`, {
        description: describeLockError(err),
      });
    },
  });
}
